import * as React from "react";
import { useState } from "react";
import { findUserBy, labelForUserKey, type AppUser } from "../../../services/userService";
import type { NoteRefArt } from "../../../services/vorgangService";
import { kuerzelVon } from "../shared/people";
import type { BezugKandidat } from "../../notizbloecke/bezugKandidaten";
import { KommentarEingabe, type KommentarSenden } from "../../notizbloecke/KommentarEingabe";
import { BeitragAendern, type KommentarAendern } from "../../notizbloecke/KommentarFaden";
import { KommentarText } from "../../notizbloecke/KommentarText";
import { type Kommentar } from "../../notizbloecke/kommentarModell";
import { mehrLabel, spaltenAnsicht } from "./kommentarAuswahl";
import "./vorgangV4.css";

/**
 * Die Kommentare NEBEN der Verlaufskarte (PO 18.09.2026: „Dann zieht man das
 * Ding bis hier und hat hier sein Kommentarfeld.").
 *
 * ══ WARUM NICHT MEHR DAS POPOVER ═══════════════════════════════════════════
 * Bis hierher hing an jedem Kartenkopf eine Sprechblase mit Zahl, und der
 * Faden ging als Popover darüber auf (`KommentarMarke`). Das hatte zwei
 * Nachteile, die der PO am 18.09.2026 zugleich angesprochen hat: Man SIEHT
 * nicht, was kommentiert ist, ohne zu klicken — und die rechte Hälfte der
 * Seite stand leer, während die Karte sich auf 640 px duckte.
 *
 * Jetzt nimmt die Karte die linke Hälfte, die Kommentare die rechte. Der Faden
 * steht damit offen neben dem, worauf er sich bezieht; das Popover entfällt.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ EINER STEHT DA, DER REST IST EINEN KLICK ENTFERNT ══════════════════════
 * Welcher, steht in `kommentarAuswahl.ts`; ob aufgeklappt bleibt, in
 * `kommentarAufgeklappt.ts`. Hier steht nur, wie es aussieht.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Knopf heißt IMMER „Kommentar schreiben" — auch dort, wo schon etwas
 * steht (PO 20.09.2026). „Antworten" behauptete einen Adressaten; in einer
 * Akte schreibt man zur Sache, nicht an eine Person.
 *
 * ══ JEDER KOMMENTAR HAT SEINEN EIGENEN HAKEN (PO 18.09.2026) ═══════════════
 * „Der kriegt die Info und kann dann sagen: alles klar, erledigt." Der Haken
 * am To-do erledigt den ganzen Arbeitsstrang, dieser nur die eine Bitte.
 * Abgehakt bleibt der Kommentar lesbar — durchgestrichen und grün, nicht weg.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Zeitpunkt eines Beitrags: „18.09. 14:20", mit Jahr nur außerhalb des laufenden. */
function zeitpunkt(d: Date): string {
    if (!d || Number.isNaN(d.getTime()) || d.getTime() === 0) return "";
    const tag = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
    const uhr = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    const jahr = d.getFullYear() === new Date().getFullYear() ? "" : `.${d.getFullYear()}`;
    return `${tag}${jahr} ${uhr}`;
}

/**
 * Das Kürzel kommt seit dem 22.09.2026 aus `kuerzelVon` (PO: „bei den Notizen
 * bitte die Kürzel anzeigen, nicht die eigenen Abbreviations"). Hier stand
 * vorher dieselbe Ableitung wie im Kommentarfaden — Zeichen für Zeichen
 * dieselbe Kopie, was das Auseinanderlaufen erst möglich gemacht hat.
 */

/** Der Haken im Kreis — „so ein rundes Ding, wo einfach so der Haken drin ist". */
const Haken: React.FC<{ size?: number }> = ({ size = 12 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M4.5 10.5 8 14l7.5-8" />
    </svg>
);

const Sprechblase: React.FC<{ size?: number }> = ({ size = 14 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M17 11.2a2.3 2.3 0 0 1-2.3 2.3H7.6L4 16.5v-3h-.7a2.3 2.3 0 0 1-2.3-2.3V5.8A2.3 2.3 0 0 1 3.3 3.5h11.4A2.3 2.3 0 0 1 17 5.8z" />
    </svg>
);

/**
 * Ein Beitrag. Ändern und Löschen stehen NUR am eigenen und erscheinen beim
 * Überfahren — anders als im früheren Popover, wo sie immer dastanden: dort
 * war der Faden eine eigene Fläche, hier steht er neben einer Karte und soll
 * sie nicht überschreien.
 */
const Beitrag: React.FC<{
    k: Kommentar;
    users: AppUser[];
    eigener: boolean;
    onAendern?: KommentarAendern;
    onLoeschen?: (k: Kommentar) => Promise<void>;
    /** Fehlt er, steht kein Haken da — niemand ist angemeldet. */
    onErledigt?: (k: Kommentar, erledigt: boolean) => Promise<void>;
    kandidaten?: BezugKandidat[];
    onBezugOeffnen?: (art: NoteRefArt, id: string) => void;
    mitAnhang: boolean;
}> = ({ k, users, eigener, onAendern, onLoeschen, onErledigt, kandidaten, onBezugOeffnen, mitAnhang }) => {
    const name = labelForUserKey(k.autor, users) || k.autor;
    const [bearbeitet, setBearbeitet] = useState(false);
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    const darfAendern = eigener && !!onAendern;
    const darfLoeschen = eigener && !!onLoeschen;
    /**
     * Was der Haken ZEIGT, während der Server noch antwortet. Ohne diesen
     * Zwischenstand bliebe der Kreis bis zum Neuladen des Verlaufs grau — bei
     * einer Geste, die sich wie ein Schalter anfühlt, ist das eine gefühlte
     * Sekunde Zweifel, ob der Klick angekommen ist.
     */
    const [gewollt, setGewollt] = useState<boolean | null>(null);
    const erledigt = gewollt ?? !!k.erledigtAm;

    const abhaken = async () => {
        if (!onErledigt || busy) return;
        const ziel = !erledigt;
        setGewollt(ziel);
        setBusy(true);
        setFehler(null);
        try {
            await onErledigt(k, ziel);
        } catch (err: any) {
            console.error("[KommentarSpalte] Haken setzen fehlgeschlagen", err);
            // Zurück auf das, was der Server weiß — sonst stünde ein Haken da,
            // den niemand außer diesem Fenster kennt.
            setGewollt(null);
            setFehler(err?.message ?? "Der Haken konnte nicht gesetzt werden.");
        } finally {
            setBusy(false);
        }
    };

    const loeschen = async () => {
        if (!onLoeschen || busy) return;
        setBusy(true);
        setFehler(null);
        try {
            await onLoeschen(k);
            // Kein `setBusy(false)`: der Beitrag ist weg, die Liste zeichnet
            // neu — ein Zustand an einem entfernten Knoten liefe ins Leere.
        } catch (err: any) {
            console.error("[KommentarSpalte] Kommentar löschen fehlgeschlagen", err);
            setFehler(err?.message ?? "Kommentar konnte nicht gelöscht werden.");
            setBusy(false);
        }
    };

    return (
        <div className={`vg4-ks-e${erledigt ? " getan" : ""}`} data-kommentar={k.key}>
            <span className="vg4-ks-av" aria-hidden>
                {kuerzelVon(findUserBy(users, k.autor), name)}
            </span>
            <div className="vg4-ks-b">
                {bearbeitet ? (
                    <BeitragAendern
                        k={k}
                        users={users}
                        stil="spalte"
                        {...(kandidaten ? { kandidaten } : {})}
                        mitAnhang={mitAnhang}
                        onSenden={async (text, mentions, zusatz) => {
                            // Leergeräumt heißt gelöscht (PO 13.09.2026) —
                            // dieselbe Regel wie am Notizblock.
                            if (!text.trim() && !zusatz) {
                                if (!onLoeschen) {
                                    throw new Error("Ein Kommentar ohne Text lässt sich nicht speichern.");
                                }
                                await loeschen();
                                return;
                            }
                            await onAendern!(k, text, mentions, zusatz);
                            setBearbeitet(false);
                        }}
                        onAbbrechen={() => setBearbeitet(false)}
                    />
                ) : (
                    <>
                        {/* Ein Kommentar kann Markup tragen (`#`-Chips,
                            Anhänge) — derselbe Reiniger wie am Block, damit
                            hier nichts durchkommt, was dort nicht darf. */}
                        <KommentarText
                            k={k}
                            className="vg4-ks-text"
                            {...(onBezugOeffnen ? { onBezugOeffnen } : {})}
                        />
                        <div className="vg4-ks-zeile">
                            <span className="vg4-ks-zeit">
                                {name} · {zeitpunkt(k.angelegt)}
                            </span>
                            {darfAendern || darfLoeschen ? (
                                <span className="vg4-ks-tun">
                                    {darfAendern ? (
                                        <button
                                            type="button"
                                            className="vg4-ks-tun-btn"
                                            disabled={busy}
                                            onClick={() => {
                                                setFehler(null);
                                                setBearbeitet(true);
                                            }}
                                        >
                                            Ändern
                                        </button>
                                    ) : null}
                                    {darfLoeschen ? (
                                        <button
                                            type="button"
                                            className="vg4-ks-tun-btn"
                                            disabled={busy}
                                            onClick={() => void loeschen()}
                                        >
                                            Löschen
                                        </button>
                                    ) : null}
                                </span>
                            ) : null}
                        </div>
                    </>
                )}
                {fehler ? <div className="vg4-ks-fehler">{fehler}</div> : null}
            </div>
            {/* DER HAKEN GEHÖRT JEDEM, DER DEN VORGANG SIEHT (PO 20.09.2026:
                „erstmal jeder") — nicht nur dem Autor, nicht nur Bearbeitern:
                gebeten wird der Erwähnte. Er steht deshalb an JEDEM Beitrag,
                auch am fremden. */}
            {onErledigt ? (
                <button
                    type="button"
                    className="vg4-ks-hak"
                    aria-pressed={erledigt}
                    disabled={busy}
                    title={erledigt ? "Haken zurücknehmen" : "Als erledigt abhaken"}
                    aria-label={erledigt ? "Haken zurücknehmen" : "Als erledigt abhaken"}
                    onClick={() => void abhaken()}
                >
                    <Haken />
                </button>
            ) : null}
        </div>
    );
};

export type KommentarSpalteProps = {
    kommentare: Kommentar[];
    users: AppUser[];
    /** Worauf sich der Faden bezieht — nur für Vorleser, sichtbar steht es links. */
    titel: string;
    /** Steht der ganze Faden da? Der Zustand gehört der Liste, nicht der Spalte. */
    auf: boolean;
    onUmschalten: (auf: boolean) => void;
    /** Fehlt er, ist der Faden nur zu lesen (kein Schreibrecht am Vorgang). */
    onNeu?: KommentarSenden;
    /** Wer liest — nur am eigenen Beitrag stehen „Ändern" und „Löschen". */
    aktuellerUserId?: string;
    onAendern?: KommentarAendern;
    onLoeschen?: (k: Kommentar) => Promise<void>;
    /**
     * Den Haken setzen oder zurücknehmen. Fehlt er, steht an keinem Beitrag
     * einer — dann liest hier jemand ohne Anmeldung mit.
     */
    onErledigt?: (k: Kommentar, erledigt: boolean) => Promise<void>;
    /** Das Angebot hinter `#` im Schreibfeld (PO 28.09.2026). */
    kandidaten?: BezugKandidat[];
    /** Klick auf einen Chip oder Anhang im Kommentar. */
    onBezugOeffnen?: (art: NoteRefArt, id: string) => void;
    /** Anhänge erlaubt — nur, wenn `onNeu`/`onAendern` den Zusatz auswerten. */
    mitAnhang?: boolean;
    /**
     * Der Schlüssel des Fadens (`zielSchluessel`) — steht als `data-faden` am
     * Element, damit die Sprungmarke einer Mitteilung ihn findet.
     */
    fadenSchluessel?: string;
};

export const KommentarSpalte: React.FC<KommentarSpalteProps> = ({
    kommentare,
    users,
    titel,
    auf,
    onUmschalten,
    onNeu,
    aktuellerUserId,
    onAendern,
    onLoeschen,
    onErledigt,
    kandidaten,
    onBezugOeffnen,
    mitAnhang = false,
    fadenSchluessel,
}) => {
    const [schreibt, setSchreibt] = useState(false);

    // Nichts zu lesen und nichts zu tun: dann bleibt die rechte Hälfte leer,
    // statt einen Knopf anzubieten, den niemand drücken darf.
    if (!kommentare.length && !onNeu) return null;

    const { zeigen, versteckt, offenVersteckt } = spaltenAnsicht(kommentare, auf);

    return (
        <div className="vg4-ks" role="group" aria-label={titel} data-faden={fadenSchluessel}>
            {zeigen.map((k) => (
                <Beitrag
                    key={k.key}
                    k={k}
                    users={users}
                    eigener={!!aktuellerUserId && k.autor === aktuellerUserId}
                    {...(onAendern ? { onAendern } : {})}
                    {...(onLoeschen ? { onLoeschen } : {})}
                    {...(onErledigt ? { onErledigt } : {})}
                    {...(kandidaten ? { kandidaten } : {})}
                    {...(onBezugOeffnen ? { onBezugOeffnen } : {})}
                    mitAnhang={mitAnhang}
                />
            ))}

            {versteckt > 0 ? (
                <button type="button" className="vg4-ks-mehr" onClick={() => onUmschalten(true)}>
                    {mehrLabel(versteckt)}
                    {/* „2 weitere · 1 offen" — ohne die Zahl sähe der Knopf aus
                        wie Beiwerk; mit ihr sagt er, dass sich das Aufklappen
                        lohnt. */}
                    {offenVersteckt > 0 ? (
                        <span className="vg4-ks-offen"> · {offenVersteckt} offen</span>
                    ) : null}
                </button>
            ) : auf && kommentare.length > 1 ? (
                <button type="button" className="vg4-ks-mehr" onClick={() => onUmschalten(false)}>
                    weniger
                </button>
            ) : null}

            {onNeu && schreibt ? (
                <div className="vg4-ks-neu-feld">
                    <KommentarEingabe
                        users={users}
                        {...(kandidaten ? { kandidaten } : {})}
                        stil="spalte"
                        ariaLabel="Kommentar schreiben"
                        placeholder={
                            kandidaten?.length
                                ? "Kurz sagen, was zu tun ist — @ erwähnt, # verweist"
                                : "Kurz sagen, was zu tun ist — @ erwähnt jemanden"
                        }
                        autoFocus
                        mitAnhang={mitAnhang}
                        onSenden={async (text, mentions, zusatz) => {
                            await (zusatz ? onNeu(text, mentions, zusatz) : onNeu(text, mentions));
                            setSchreibt(false);
                        }}
                        onAbbrechen={() => setSchreibt(false)}
                    />
                </div>
            ) : onNeu ? (
                <button
                    type="button"
                    className="vg4-ks-neu"
                    onClick={() => {
                        // Wer schreibt, soll sehen, worunter er schreibt: der
                        // Faden klappt mit auf. Sonst stünde der neue Satz
                        // gleich darauf hinter „2 weitere".
                        if (kommentare.length > 1) onUmschalten(true);
                        setSchreibt(true);
                    }}
                >
                    <Sprechblase />
                    Kommentar schreiben
                </button>
            ) : null}

        </div>
    );
};
